import { useMemo } from "react";
import { object } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { Tables, hashCode } from "@repo/utils";
import { ELEMENT_TYPES } from "@utils";
import {
  useVisibilityModeCellClassName,
  useEstimateEditorSettings,
  useEstimateTableSettings,
  useTableExpandHandler,
  useEstimateStructure,
  PinnableCell,
  ExpandContainer,
  DragHandleCell,
} from "@tools";
import { Collapse, Grid } from "@material-ui/core";
import { TimelineUtilizationRowGroupList } from "./TimelineUtilizationRowGroupList";
import { WorkTypeActions } from "./components/WorkTypeActions";
import classnames from "classnames";

export const TimelineUtilizationWorkType = observer(({
  workType,
}) => {
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  const { allowEdition, blockExpansions, isPdfPrinterView } = useEstimateEditorSettings();
  const { useExpandCell, fullSectionActions } = useEstimateTableSettings();

  const {
    usedRoundDecimals,
    usedThousandSeparator,
  } = structure.settings;

  const {
    id,
    name,
    displayHours,
    treePath,
    isTimelineOpened,
    displayTimelineResources,
    isHidden,
    hideOnTimeline,
  } = workType;
  
  const className = useVisibilityModeCellClassName(isHidden || hideOnTimeline);

  const expandable = useMemo(() => Boolean(
    displayTimelineResources?.length
  ), [ displayTimelineResources ]);
  
  const expanded = useMemo(() => Boolean(
    expandable && (isTimelineOpened || blockExpansions)
  ), [ isTimelineOpened, blockExpansions, expandable ]);
  
  const hashedPath = useMemo(() => (
    [Tables.TIMELINE, ...treePath].map((id) => hashCode(id))
  ), [ treePath ]);

  const handleResourceReorder = (list) => workType.reorderResources(list, true);
  
  const { handleOpen, ...collapseProps } = useTableExpandHandler({
    expandable: expandable,
    isOpened: isTimelineOpened,
    onExpand: workType.setTimelineOpenState
  });
  
  return (
    <Grid
      item container
      direction="column"
      style={{ opacity: workType.isTurnedOff ? 0.3 : undefined }}
    >
      <Grid
        item container
        wrap="nowrap"
        className={classnames(
          "hoverRow bg-secondary preset-page",
          expandable && "cursor-pointer",
          className,
        )}
        onClick={handleOpen}
      >
        {
          !isPdfPrinterView && <DragHandleCell displayHandle={allowEdition} />
        }
        <PinnableCell
          boldText
          hasStaticWidth
          allowOverflowDisplay
          tableDepth={2}
          wider={fullSectionActions}
          actions={
            <NumericFormat
              displayType="text"
              value={displayHours}
              thousandSeparator={usedThousandSeparator}
              decimalSeparator="."
              suffix={t("times.h")}
              decimalScale={usedRoundDecimals}
              valueIsNumericString
              className="w-max text-lighter text-sm"
            />
          }
          containers={
            useExpandCell &&
            <ExpandContainer
              tableDepth={2}
              expandable={expandable}
              expanded={expanded}
            />
          }
        >
          <Grid
            item
            container
            alignItems="center"
            className="w-max"
          >
            <span
              className={classnames(
                "name preset-timelineTitleText text-md",
                !name?.length && "semi-transparent"
              )}
            >
              { name || t("common.unnamed") }
            </span>
            <WorkTypeActions
              workType={workType}
              opened={isTimelineOpened}
            />
          </Grid>
        </PinnableCell>
      </Grid>
      <Collapse
        in={expanded}
        className="w-full"
        mountOnEnter
        unmountOnExit
        data-id={hashCode(`${id}_Tcont`)}
        data-p={treePath.join("/")}
        data-t={ELEMENT_TYPES.TIMELINE_RESOURCE}
        { ...collapseProps }
      >
        <TimelineUtilizationRowGroupList
          listElements={displayTimelineResources}
          onReorder={handleResourceReorder}
          disabled={!allowEdition}
          hashedPath={hashedPath}
          renderTeamMembers
        />
      </Collapse>
    </Grid>
  );
});

TimelineUtilizationWorkType.propTypes = {
  workType: object.isRequired,
};
